<template>
  <form @submit.prevent>
    <div>
      <h2>
        <span v-if="isEditMode">{{ $t("risk.definition.name") }} "{{ editedEntity.name | translate }}" {{ $t("common.update") }}</span>
        <span v-else>{{ $t("risk.definition.new") }}</span>
      </h2>

      <b-form-tags v-model="form.products" size="lg" class="mb-2" add-on-change no-outer-focus>
        <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
          <b-form-select v-bind="inputAttrs" v-on="inputHandlers" :disabled="disabled || products.length === 0" :options="scopedProducts">
            <template #first>
              <!-- This is required to prevent bugs with Safari -->
              <option disabled value=""> {{ $t("product.text") }}</option>
            </template>
          </b-form-select>

          <ul v-if="tags.length > 0" class="list-inline d-inline-block mt-2">
            <li v-for="tag in tags" :key="tag" class="list-inline-item">
              <b-form-tag @remove="removeTag(tag)" :title="tag" :disabled="disabled" variant="primary">{{ tag }}</b-form-tag>
            </li>
          </ul>
          <b-form-text v-else>
            <span class="text-danger">{{ $t("product.required") }}</span>
          </b-form-text>
        </template>
      </b-form-tags>

      <sweet-input
        v-model="form.name"
        :isValid="!$v.form.name.$error"
        @input="$v.form.name.$touch()"
        layout="vertical"
        :placeholder="$t('risk.definition.name')"
        :label="$t('risk.definition.name')"
      />

      <h6 class="mt-4">{{ $t("risk.category.name") }}</h6>
      <select class="form-control " v-model="form.category">
        <option value="ADMINISTRATIVE">{{ $t("risk.category.administrative") }}</option>
        <option value="TECHNICAL">{{ $t("risk.category.technical") }}</option>
        <option value="LEGAL">{{ $t("risk.category.legal") }}</option>
      </select>

      <br />
      <sweet-input
        :isValid="!$v.form.effect.$error"
        @input="$v.form.effect.$touch()"
        v-model="form.effect"
        type="number"
        layout="vertical"
        :placeholder="$t('risk.effect')"
        :label="$t('risk.effect')"
      />

      <div class="col-mr-5 mt-3">
        <right-panel
          class="float-right mt-1 "
          id="sidebar-new-threat-definition"
          width="650px"
          :icon="'mdi mdi-plus'"
          :text="$t('risk.threat-definition.new')"
          @hidden="threatRightPanelClosed"
        >
          <div class="pl-5 pr-5">
            <ThreatForm :hide-library="true" @onSave="onThreatDefinitionSelect" />
          </div>
        </right-panel>
      </div>
      <h6 class="mt-4">{{ $t("risk.threat-definition.text") }}</h6>
      <filter-select
        :validation="true"
        :isValid="!$v.form.threats.$invalid"
        id="threatFilter"
        ref="threatFilter"
        :placeholder="$t('risk.threat-definition.text')"
        :multiple="true"
        @filter="onThreatDefinitionSearch"
        :selected="form.threats"
        @changed="onThreatDefinitionSelect"
      />

      <div class="col-mr-5 mt-3">
        <right-panel
          class="float-right mt-1 "
          id="sidebar-new-weakness-definition"
          width="650px"
          :icon="'mdi mdi-plus'"
          :text="$t('risk.weakness-definition.new')"
          @hidden="weaknessRightPanelClosed"
        >
          <div class="pl-5 pr-5">
            <WeaknessForm :hide-library="true" @onSave="onWeaknessDefinitionSelect" />
          </div>
        </right-panel>
      </div>
      <h6 class="mt-4">{{ $t("risk.weakness-definition.text") }}</h6>
      <filter-select
        :validation="true"
        :isValid="!$v.form.weaknesses.$invalid"
        id="weaknessFilter"
        ref="weaknessFilter"
        :placeholder="$t('risk.weakness-definition.text')"
        :multiple="true"
        @filter="onWeaknessDefinitionSearch"
        :selected="form.weaknesses"
        @changed="onWeaknessDefinitionSelect"
      />

      <sweet-button @click="save()" type="submit" :loading="isUpdating" :disabled="isUpdating || $v.$invalid" variant="success" icon="mdi mdi-check" size="sm" buttonClass="mt-2">
        {{ $t("common.save") }}
      </sweet-button>
    </div>
  </form>
</template>

<script>
import { maxLength, minLength, required } from "vuelidate/lib/validators";
import ThreatForm from "./workspace-library-risk-threat-definition-form";
import WeaknessForm from "./workspace-library-risk-weakness-definition-form";

const state = "workspaceRiskDefinition";

export default {
  components: {
    ThreatForm,
    WeaknessForm,
  },
  data() {
    return {
      form: {
        name: undefined,
        category: "ADMINISTRATIVE",
        weaknesses: [],
        threats: [],
      },
      formError: undefined,
    };
  },
  validations: {
    form: {
      name: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(350),
      },
      threats: { required },
      weaknesses: { required },
      effect: {
        required,
      },
      category: {
        required,
      },
    },
  },

  watch: {
    editedEntity: {
      deep: true,
      immediate: true,
      handler(v) {
        this.form = { ...this.prepareFormForEdit(v) };
      },
    },
  },
  computed: {
    isUpdating() {
      return this.$store.getters[`${state}/isUpdating`];
    },
    editedEntity() {
      return this.$store.getters[`${state}/editedEntity`];
    },
    isEditMode() {
      return this.$store.getters[`${state}/isEditMode`];
    },
  },
  mounted() {},
  methods: {
    async save() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`${state}/save`, this.form);
        this.form = {};
        this.$toast.success(this.$t("common.success"));
        this.$root.$emit("bv::toggle::collapse", "sidebar-new-risk-definition");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    threatRightPanelClosed() {
      this.$refs.threatFilter.reset();
    },
    weaknessRightPanelClosed() {
      this.$refs.weaknessFilter.reset();
    },

    async onThreatDefinitionSearch(value, callback) {
      const response = await this.$store.dispatch(`workspaceRiskThreatDefinition/paginate`, {
        options: { limit: 200 },
        query: { name: { value }, "scope.value": { value: "workspace", isObject: true } },
      });
      callback(response.data?.docs ?? []);
    },
    onThreatDefinitionSelect(threats) {
      this.form = { ...this.form, threats };
    },

    async onWeaknessDefinitionSearch(value, callback) {
      const response = await this.$store.dispatch(`workspaceRiskWeaknessDefinition/paginate`, {
        options: { limit: 200 },
        query: { name: { value }, "scope.value": { value: "workspace", isObject: true } },
      });
      callback(response.data?.docs ?? []);
    },
    onWeaknessDefinitionSelect(weaknesses) {
      this.form = { ...this.form, weaknesses };
    },
  },
};
</script>
